import React, { useState, useRef } from 'react';
import TimelineEditor from '../components/TimelineEditor';
import AudioBar from '../components/AudioBar';
import Card from '../ui/CardComponent';
import { Video, Upload } from 'lucide-react';

export default function TimelineDemo() {
  const [selectedAudio, setSelectedAudio] = useState<any>(null);
  const [showTimeline, setShowTimeline] = useState(true);
  const [uploadedVideo, setUploadedVideo] = useState<File | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Mock uploaded audio examples
  const mockAudioExamples = [
    {
      id: 'demo1',
      title: 'Summer Vibes',
      artist: 'DJ Wave',
      duration: '2:34',
      thumbnail: '🎵',
      waveform: [0.3, 0.5, 0.7, 0.9, 0.6, 0.4, 0.8, 0.5, 0.3, 0.6, 0.7, 0.4, 0.5, 0.8, 0.6, 0.3, 0.4, 0.6, 0.8, 0.7, 0.5, 0.6, 0.8, 0.9, 0.7, 0.5, 0.4, 0.6, 0.5, 0.7, 0.6, 0.4]
    },
    {
      id: 'sfx1',
      title: 'Swoosh Transition',
      artist: 'SFX Library',
      duration: '0:02',
      thumbnail: '💨',
      category: 'Transitions',
      waveform: [0.2, 0.8, 0.9, 1.0, 0.8, 0.3]
    }
  ];

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setUploadedVideo(file);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Hidden file input */}
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoUpload}
        className="hidden"
      />

      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-[1600px] mx-auto px-6 py-6">
          <h1 className="text-2xl font-semibold text-[#111827] mb-2">Timeline Editor Demo</h1>
          <p className="text-sm text-[#6B7280]">
            Interactive audio timeline for video post scheduling
          </p>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">
        {/* Video Upload/Preview */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#111827] mb-1">Video Upload</h2>
                <p className="text-sm text-[#6B7280]">
                  {uploadedVideo ? 'Video ready for editing' : 'Upload a video to see it in the timeline'}
                </p>
              </div>
              {!uploadedVideo && (
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="px-4 py-2 bg-gradient-to-r from-[#F67570] to-[#F99B97] text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <Upload size={16} />
                  <span className="text-sm font-medium">Upload Video</span>
                </button>
              )}
            </div>
          </div>
          <div className="p-6">
            {uploadedVideo ? (
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center text-white">
                    <Video size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900">{uploadedVideo.name}</p>
                    <p className="text-xs text-green-700 mt-1">
                      {(uploadedVideo.size / 1024 / 1024).toFixed(2)} MB • {uploadedVideo.type}
                    </p>
                  </div>
                  <button
                    onClick={() => setUploadedVideo(null)}
                    className="px-3 py-1.5 text-xs font-medium text-green-700 hover:text-green-900 hover:bg-green-100 rounded transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-700 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-500">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                    <Video size={40} className="text-white" />
                  </div>
                  <p className="text-white text-lg font-medium mb-2">No Video Uploaded</p>
                  <p className="text-white/60 text-sm mb-4">Upload a video to see the preview in timeline</p>
                  <button
                    onClick={() => videoInputRef.current?.click()}
                    className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    Choose Video File
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Audio Integration Bar */}
        <AudioBar onAudioSelect={(track) => setSelectedAudio(track)} />

        {/* Timeline Editor */}
        {showTimeline && (
          <TimelineEditor
            videoDuration={120}
            uploadedAudio={selectedAudio}
            uploadedVideo={uploadedVideo}
            onAudioUpdate={(blocks) => console.log('Timeline updated:', blocks)}
          />
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button
            onClick={() => videoInputRef.current?.click()}
            className="p-4 border-2 border-dashed border-[#E5E7EB] hover:border-blue-500 rounded-lg transition-all bg-white group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center text-2xl">
                🎬
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold text-[#111827]">Upload Video</div>
                <div className="text-xs text-[#6B7280]">See preview in timeline</div>
              </div>
            </div>
          </button>

          <button
            onClick={() => setSelectedAudio(mockAudioExamples[0])}
            className="p-4 border-2 border-dashed border-[#E5E7EB] hover:border-[#F67570] rounded-lg transition-all bg-white group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#F67570]/20 to-[#F99B97]/20 flex items-center justify-center text-2xl">
                🎵
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold text-[#111827]">Add Music Track</div>
                <div className="text-xs text-[#6B7280]">Full-length background music</div>
              </div>
            </div>
          </button>

          <button
            onClick={() => setSelectedAudio(mockAudioExamples[1])}
            className="p-4 border-2 border-dashed border-[#E5E7EB] hover:border-purple-500 rounded-lg transition-all bg-white group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center text-2xl">
                💨
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold text-[#111827]">Add Sound Effect</div>
                <div className="text-xs text-[#6B7280]">Short transition or impact</div>
              </div>
            </div>
          </button>

          <button
            onClick={() => setShowTimeline(!showTimeline)}
            className="p-4 border-2 border-dashed border-[#E5E7EB] hover:border-blue-500 rounded-lg transition-all bg-white group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                {showTimeline ? '👁️' : '🚫'}
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold text-[#111827]">
                  {showTimeline ? 'Hide' : 'Show'} Timeline
                </div>
                <div className="text-xs text-[#6B7280]">Toggle editor visibility</div>
              </div>
            </div>
          </button>
        </div>

        {/* Features Overview */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-semibold text-[#111827]">Timeline Features</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">✓</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#111827]">Drag to Position</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    Click and drag audio blocks to any point in the video timeline
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">✂️</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#111827]">Trim Audio</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    Drag the edges of blocks to trim start and end points
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">▶️</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#111827]">Preview Playback</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    Play/pause to preview audio sync with video timeline
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">📊</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#111827]">Waveform Visual</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    See audio waveforms inside each block for better alignment
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🎯</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#111827]">Precise Timing</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    Click timeline to jump playhead to exact timestamps
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🎬</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#111827]">Video Preview</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    Upload videos to see live preview synced with timeline playback
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🗑️</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#111827]">Easy Removal</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    Select blocks to see delete button and remove unwanted audio
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Usage Instructions */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB] bg-gradient-to-r from-blue-50 to-purple-50">
            <h2 className="text-lg font-semibold text-[#111827]">How to Use</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#F67570] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                1
              </div>
              <div>
                <p className="text-sm font-medium text-[#111827]">Upload a video file (Optional)</p>
                <p className="text-xs text-[#6B7280] mt-1">
                  Click "Upload Video" button above to add your video and see it preview in the timeline
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#F67570] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                2
              </div>
              <div>
                <p className="text-sm font-medium text-[#111827]">Add audio from the Audio Bar</p>
                <p className="text-xs text-[#6B7280] mt-1">
                  Choose from trending music, sound effects, or use AI recognition
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#F67570] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                3
              </div>
              <div>
                <p className="text-sm font-medium text-[#111827]">Position and trim in the timeline</p>
                <p className="text-xs text-[#6B7280] mt-1">
                  Drag blocks to move them, drag edges to trim, click to select
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#F67570] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                4
              </div>
              <div>
                <p className="text-sm font-medium text-[#111827]">Preview and adjust</p>
                <p className="text-xs text-[#6B7280] mt-1">
                  Use play/pause controls to preview your audio timing with the video
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
